<template>
  <div class="admin-settings-wrapper px-2 px-md-4 py-2 py-md-4">
    <b-overlay :show="isLoading" rounded="sm">
      <div class="admin-settings bg-white-custom border p-3" :class="isDarkMode ? 'dark' : ''">
        <div
          class="admin-settings--setting-tabs d-flex flex-column"
          :class="isDarkMode ? 'dark' : ''"
        >
          <div class="admin-settings--setting-tabs--header d-flex align-items-center">
            <div
              class="settings-tab-header"
              :class="{
                active: tabSelected === 0,
                'border-info': tabSelected === 0,
                'text-info': tabSelected === 0,
              }"
              @click="() => { tabSelected = 0; }"
            >
              Agency Tenant
            </div>
            <div
              class="settings-tab-header"
              :class="{
                active: tabSelected === 1,
                'border-info': tabSelected === 1,
                'text-info': tabSelected === 1,
              }"
              @click="() => { tabSelected = 1; }"
            >
              Conversations & Messaging
            </div>
            <div
              class="settings-tab-header"
              :class="{
                active: tabSelected === 2,
                'border-info': tabSelected === 2,
                'text-info': tabSelected === 2,
              }"
              @click="() => { tabSelected = 2; }"
            >
              Video
            </div>
            <div
              class="settings-tab-header"
              :class="{
                active: tabSelected === 3,
                'border-info': tabSelected === 3,
                'text-info': tabSelected === 3,
              }"
              @click="() => { tabSelected = 3; }"
            >
              Location
            </div>
          </div>
          <div class="admin-settings--setting-tabs--content border">
            <div v-show="tabSelected === 0" class="settings-tab-content">
              <div class="row d-flex admin-settings--tenant-info">
                <div class="col-lg-3 col-md-6 col-xs-12 mb-3">
                  <div class="setting-item" :class="isDarkMode ? 'dark' : ''">
                    <div>
                      <label class="setting-item--label">Tenant ID</label>
                      <div class="setting-item--content">
                        <code>{{ agency["agency_id"] }}</code>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6 col-xs-12 mb-3">
                  <div class="setting-item" :class="isDarkMode ? 'dark' : ''">
                    <div>
                      <label class="setting-item--label">Region</label>
                      <div class="setting-item--content d-flex align-items-center flex-nowrap">
                        <img width="20px" src="../../assets/images/flags/us.svg" />
                        <span class="ml-1 text-nowrap font-weight-medium">
                          {{ agency["region"] }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6 col-xs-12 mb-3">
                  <div class="setting-item" :class="isDarkMode ? 'dark' : ''">
                    <div>
                      <label class="setting-item--label">Environment</label>
                      <div class="setting-item--content">
                        <b-badge :variant="badgeTheme[agency.environment] || 'info'">
                          {{ agency.environment }}
                        </b-badge>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6 col-xs-12 mb-3">
                  <div class="setting-item" :class="isDarkMode ? 'dark' : ''">
                    <div>
                      <label class="setting-item--label">Status</label>
                      <div class="setting-item--content">
                        <b-badge :variant="badgeTheme[agency.status] || 'info'">
                          {{ agency.status }}
                        </b-badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row d-flex align-items-center admin-settings--tenant-settings">
                <div class="col-xl-3 col-md-6">
                  <div class="logo-upload">
                    <div class="logo-upload--btn">
                      <img
                        :src="logoPreview"
                        v-if="logoPreview"
                        class="logo-responsive"
                      />
                      <b-icon icon="camera-fill" font-scale="2" v-else></b-icon>
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        @input="pickFile"
                        @change="touchSetting($refs.agencyLogo)"
                        ref="agencyLogo"
                        name="agencyLogo"
                      />
                    </div>
                  </div>
                  <p
                    class="logo-upload--label"
                    @click="triggerFileUpload"
                    :class="isDarkMode ? 'dark' : ''"
                  >
                    Change <br />
                    Agency Logo
                  </p>
                </div>
                <div class="col-xl-9 col-md-6">
                  <div class="row d-flex">
                    <div class="col-xl-6 col-md-12">
                      <b-form-group label="Tenant Name">
                        <b-form-input
                          name="agency_name"
                          :value="agency.agency_name"
                          ref="agency_name"
                          @change="touchSetting($refs.agency_name, 'agency')"
                        ></b-form-input>
                      </b-form-group>
                      <b-form-group label="Agency Domain">
                        <b-form-input
                          name="agency_domain"
                          v-model="agency.agency_domain"
                          ref="agency_domain"
                          @change="touchSetting($refs.agency_domain, 'agency')"
                          placeholder="google.com"
                        ></b-form-input>
                      </b-form-group>
                    </div>
                    <div class="col-xl-6 col-md-12">
                      <div class="row d-flex align-items-center h-100">
                        <div class="form-check form-switch m-3">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="twoFAForAll"
                            name="twoFAForAll"
                            :checked="settings.twoFAForAll"
                            @change="touchSetting($event.target)"
                          />
                          <label class="form-check-label" for="twoFAForAll">
                            Enable Two Factor Authentication (2FA)
                          </label>
                        </div>

                        <div class="form-check form-switch m-3">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="autoEnrollUserByEmail"
                            name="autoEnrollUserByEmail"
                            :checked="settings.autoEnrollUserByEmail"
                            @change="touchSetting($event.target)"
                          />
                          <label class="form-check-label" for="autoEnrollUserByEmail">
                            Automatically add users by domain
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-show="tabSelected === 1" class="settings-tab-content flexible">
              <div
                class="admin-settings--settings-group border"
                :class="{
                  'disabled': !settings.autoCloseEnabled
                }"
              >
                <div class="parent-setting-item bg-white-custom form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="autoCloseEnabled"
                    name="autoCloseEnabled"
                    v-model="settings.autoCloseEnabled"
                    :checked="settings.autoCloseEnabled"
                    @change="touchSetting($event.target)"
                  />
                  <label class="form-check-label" for="autoCloseEnabled">
                    Automatically Close Conversations
                  </label>
                </div>
                <b-form-group label="Auto Close Conversation Time (in minutes)">
                  <b-form-input
                    name="closedConversationTimeout"
                    v-model="settings.closedConversationTimeout"
                    @change="touchSetting($refs.closedConversationTimeout)"
                    ref="closedConversationTimeout"
                    :disabled="!settings.autoCloseEnabled"
                  ></b-form-input>
                </b-form-group>
                <b-form-group label="Closed Conversation Message">
                  <b-form-textarea
                    placeholder=""
                    rows="4"
                    name="closedConversationMessage"
                    v-model="settings.closedConversationMessage"
                    @change="touchSetting($refs.closedConversationMessage)"
                    ref="closedConversationMessage"
                    :disabled="!settings.autoCloseEnabled"
                  ></b-form-textarea>
                </b-form-group>
              </div>
              <div
                class="admin-settings--settings-group border"
                :class="{
                  'disabled': !settings.inboundConversationsEnabled
                }"
              >
                <div class="parent-setting-item bg-white-custom form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="inboundConversationsEnabled"
                    name="inboundConversationsEnabled"
                    v-model="settings.inboundConversationsEnabled"
                    :checked="settings.inboundConversationsEnabled"
                    @change="touchSetting($event.target)"
                  />
                  <label class="form-check-label" for="inboundConversationsEnabled">
                    New Inbound Conversation
                  </label>
                </div>
                <b-form-group label="Inbound Conversation Receipt Message">
                  <b-form-textarea
                    placeholder="Automated message to send when a new conversation is initiated by an external recipient..."
                    rows="4"
                    name="inboundConversationReceiptMessage"
                    v-model="settings.inboundConversationReceiptMessage"
                    ref="inboundConversationReceiptMessage"
                    @change="
                      touchSetting($refs.inboundConversationReceiptMessage)
                    "
                    :disabled="!settings.inboundConversationsEnabled"
                  ></b-form-textarea>
                </b-form-group>
              </div>
              <div class="pl-1">
                <div class="form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="enableConversationAssignment"
                    name="enableConversationAssignment"
                    v-model="settings.enableConversationAssignment"
                    :checked="settings.enableConversationAssignment"
                    @change="touchSetting($event.target)"
                  />
                  <label class="form-check-label" for="enableConversationAssignment">
                    Agent Assignment
                  </label>
                </div>
              </div>
              <div class="pl-1">
                <div class="form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="enableInternational"
                    name="enableInternational"
                    v-model="settings.enableInternational"
                    :checked="settings.enableInternational"
                    @change="touchSetting($event.target)"
                  />
                  <label class="form-check-label" for="enableInternational">
                    Enable International Dial code in Phone Number
                  </label>
                </div>
              </div>
              <div
                class="admin-settings--settings-group border"
                :class="{
                  'disabled': !localSoundSelectorSettings.textNotificationSound.enabled
                }"
              >
                <div class="parent-setting-item bg-white-custom form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    :checked="
                      localSoundSelectorSettings.textNotificationSound.enabled
                    "
                    @change="
                      changeSoundSelectorSettings('textNotificationSound', {
                        enabled:
                          !localSoundSelectorSettings.textNotificationSound
                            .enabled,
                      })
                    "
                  />
                  <label class="form-check-label">
                    New Inbound SMS conversation chime
                  </label>
                </div>
                <b-form-group>
                  <div class="d-flex justify-content-between gap-2">
                    <v-select
                      appendToBody
                      :calculatePosition="withPopper"
                      :options="audioList"
                      :clearable="false"
                      @input="
                        changeSoundSelectorSettings('textNotificationSound', {
                          value: $event.value,
                        })
                      "
                      :value="
                        localSoundSelectorSettings.textNotificationSound.value
                      "
                      :disabled="
                        !localSoundSelectorSettings.textNotificationSound
                          .enabled
                      "
                      placeholder="-- Choose Optional --"
                      class="w-100"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          v-on="events"
                        />
                      </template>
                    </v-select>
                    <b-button
                      variant="light"
                      pill
                      size="xs"
                      @click="testAudio('textNotificationSound')"
                      :disabled="
                        !localSoundSelectorSettings.textNotificationSound
                          .enabled ||
                        (audioTesting &&
                          audioTesting.status === true &&
                          audioTesting.type !== 'textNotificationSound')
                      "
                    >
                      <b-icon
                        icon="stop-fill"
                        font-scale="1.5"
                        v-if="
                          audioTesting &&
                          audioTesting.status === true &&
                          audioTesting.type === 'textNotificationSound'
                        "
                      ></b-icon>
                      <b-icon
                        icon="play-fill"
                        font-scale="1.5"
                        v-else
                      ></b-icon>
                    </b-button>
                  </div>
                </b-form-group>
              </div>
              <div class="admin-settings--settings-group border">
                <div class="parent-setting-item bg-white-custom form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    :checked="
                      localSoundSelectorSettings.messageNotificationSound
                        .enabled
                    "
                    @change="
                      changeSoundSelectorSettings(
                        'messageNotificationSound',
                        {
                          enabled:
                            !localSoundSelectorSettings
                              .messageNotificationSound.enabled,
                        }
                      )
                    "
                  />
                  <label class="form-check-label">
                    Inbound Message Notification chime
                  </label>
                </div>
                <b-form-group>
                  <div class="d-flex justify-content-between gap-2">
                    <v-select
                      :options="audioList"
                      :clearable="false"
                      placeholder="-- Choose Optional --"
                      class="w-100"
                      appendToBody
                      :calculatePosition="withPopper"
                      @input="
                        changeSoundSelectorSettings(
                          'messageNotificationSound',
                          { value: $event.value }
                        )
                      "
                      :value="
                        localSoundSelectorSettings.messageNotificationSound
                          .value
                      "
                      :disabled="
                        !localSoundSelectorSettings.messageNotificationSound
                          .enabled
                      "
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          v-on="events"
                        />
                      </template>
                    </v-select>
                    <b-button
                      variant="light"
                      size="xs"
                      pill
                      @click="testAudio('messageNotificationSound')"
                      :disabled="
                        !localSoundSelectorSettings.messageNotificationSound
                          .enabled ||
                        (audioTesting &&
                          audioTesting.status === true &&
                          audioTesting.type !== 'messageNotificationSound')
                      "
                      ><b-icon
                        icon="stop-fill"
                        font-scale="1.5"
                        v-if="
                          audioTesting &&
                          audioTesting.status === true &&
                          audioTesting.type === 'messageNotificationSound'
                        "
                      ></b-icon>
                      <b-icon
                        icon="play-fill"
                        font-scale="1.5"
                        v-else
                      ></b-icon
                    ></b-button>
                  </div>
                </b-form-group>
              </div>
            </div>
            <div v-show="tabSelected === 2" class="settings-tab-content flexible">
              <div
                class="admin-settings--settings-group border"
                :class="{
                  'disabled': !settings.videoEnabled
                }"
              >
                <div class="parent-setting-item bg-white-custom form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="videoEnabled"
                    name="videoEnabled"
                    v-model="settings.videoEnabled"
                    :checked="settings.videoEnabled === true"
                    @change="touchSetting($event.target)"
                  />
                  <label class="form-check-label" for="videoEnabled">
                    Video Enabled
                  </label>
                </div>
                <div class="row">
                  <div class="col-12 mb-3">
                    <div class="form-check form-switch">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="video:enableAgentVideo"
                        name="video:enableAgentVideo"
                        :checked="settings['video:enableAgentVideo'] === true"
                        @change="touchSetting($event.target)"
                        :disabled="!settings.videoEnabled"
                      />
                      <label class="form-check-label" for="video:enableAgentVideo">
                        Enable Agent Video
                      </label>
                    </div>
                  </div>
                  <div class="col-12 mb-3">
                    <div class="form-check form-switch">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="enableVideoTranscription"
                        name="enableVideoTranscription"
                        :checked="settings.enableVideoTranscription === true"
                        @change="touchSetting($event.target)"
                        :disabled="!settings.videoEnabled"
                      />
                      <label class="form-check-label" for="enableVideoTranscription">
                        Video Transcription
                      </label>
                    </div>
                  </div>
                  <div class="col-12 mb-3">
                    <div class="form-check form-switch">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="enableVideoRecording"
                        name="enableVideoRecording"
                        :checked="settings.enableVideoRecording"
                        @change="touchSetting($event.target)"
                        :disabled="!settings.videoEnabled"
                      />
                      <label class="form-check-label" for="enableVideoRecording">
                        Video Recording
                      </label>
                    </div>
                  </div>
                  <div class="col-12 mb-3">
                    <div class="form-check form-switch">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="enableVideoRecipientControls"
                        name="enableVideoRecipientControls"
                        :checked="
                          settings['enableVideoRecipientControls'] ===
                          true
                        "
                        @change="touchSetting($event.target)"
                        :disabled="!settings.videoEnabled"
                      />
                      <label class="form-check-label" for="enableVideoRecipientControls">
                        Enable Remote Camera Control
                      </label>
                    </div>
                  </div>
                  <div class="col-12">
                    <b-form-group label="Video Request Timeout (in minutes)">
                      <b-form-input
                        name="videoRequestTimeout"
                        v-model="settings.videoRequestTimeout"
                        @change="touchSetting($refs.videoRequestTimeout)"
                        ref="videoRequestTimeout"
                        :disabled="!settings.videoEnabled"
                      ></b-form-input>
                    </b-form-group>
                  </div>
                  <div class="col-12">
                    <b-form-group label="Video Request Help Text">
                      <b-form-textarea
                        placeholder=""
                        rows="4"
                        name="videoRequestExplainMsg"
                        v-model="settings.videoRequestExplainMsg"
                        @change="touchSetting($refs.videoRequestExplainMsg)"
                        ref="videoRequestExplainMsg"
                        :disabled="!settings.videoEnabled"
                      ></b-form-textarea>
                    </b-form-group>
                  </div>
                  <div class="col-12">
                    <b-form-group>
                      <label class="d-flex align-items-center">
                        <b-icon icon="headphones" font-scale="1.5" class="mr-2" />
                        Video Participant Joined chime
                      </label>
                      <div class="d-flex justify-content-between gap-2">
                        <v-select
                          appendToBody
                          :calculatePosition="withPopper"
                          @input="
                            changeSoundSelectorSettings(
                              'videoJoinedNotificationSound',
                              { value: $event.value }
                            )
                          "
                          :clearable="false"
                          :value="
                            localSoundSelectorSettings.videoJoinedNotificationSound
                              .value
                          "
                          :disabled="
                            !localSoundSelectorSettings.videoJoinedNotificationSound
                              .enabled || !settings.videoEnabled
                          "
                          :options="audioList"
                          placeholder="-- Choose Optional --"
                          class="w-100"
                        >
                          <template #search="{ attributes, events }">
                            <input
                              class="vs__search"
                              v-bind="attributes"
                              v-on="events"
                            />
                          </template>
                        </v-select>
                        <b-button
                          variant="light"
                          pill
                          size="xs"
                          :disabled="
                            !localSoundSelectorSettings.videoJoinedNotificationSound
                              .enabled ||
                            !settings.videoEnabled ||
                            (audioTesting &&
                              audioTesting.status === true &&
                              audioTesting.type !== 'videoJoinedNotificationSound')
                          "
                          v-b-tooltip.hover
                          title="Test audio"
                          @click="testAudio('videoJoinedNotificationSound')"
                        >
                          <b-icon
                            v-if="
                              audioTesting &&
                              audioTesting.status === true &&
                              audioTesting.type === 'videoJoinedNotificationSound'
                            "
                            icon="stop-fill"
                            font-scale="1.5"
                          />
                          <b-icon v-else icon="play-fill" font-scale="1.5" />
                        </b-button>
                      </div>
                    </b-form-group>
                  </div>
                  <div class="col-12">
                    <b-form-group>
                      <label class="d-flex align-items-center">
                        <b-icon icon="headphones" font-scale="1.5" class="mr-2" />
                        Video Participant Left chime
                      </label>
                      <div class="d-flex justify-content-between gap-2">
                        <v-select
                          :options="audioList"
                          placeholder="-- Choose Optional --"
                          :clearable="false"
                          class="w-100"
                          appendToBody
                          :calculatePosition="withPopper"
                          @input="
                            changeSoundSelectorSettings(
                              'videoLeftNotificationSound',
                              { value: $event.value }
                            )
                          "
                          :value="
                            localSoundSelectorSettings.videoLeftNotificationSound
                              .value
                          "
                          :disabled="
                            !localSoundSelectorSettings.videoLeftNotificationSound
                              .enabled || !settings.videoEnabled
                          "
                        >
                          <template #search="{ attributes, events }">
                            <input
                              class="vs__search"
                              v-bind="attributes"
                              v-on="events"
                            />
                          </template>
                        </v-select>
                        <b-button
                          variant="light"
                          pill
                          size="xs"
                          :disabled="
                            !localSoundSelectorSettings.videoLeftNotificationSound
                              .enabled ||
                            !settings.videoEnabled ||
                            (audioTesting &&
                              audioTesting.status === true &&
                              audioTesting.type !== 'videoLeftNotificationSound')
                          "
                          v-b-tooltip.hover
                          title="Test audio"
                          @click="testAudio('videoLeftNotificationSound')"
                        >
                          <b-icon
                            v-if="
                              audioTesting &&
                              audioTesting.status === true &&
                              audioTesting.type === 'videoLeftNotificationSound'
                            "
                            icon="stop-fill"
                            font-scale="1.5"
                          />
                          <b-icon v-else icon="play-fill" font-scale="1.5" />
                        </b-button>
                      </div>
                    </b-form-group>
                  </div>
                </div>
              </div>
            </div>
            <div v-show="tabSelected === 3" class="settings-tab-content flexible">
              <div
                class="admin-settings--settings-group border"
                :class="{
                  'disabled': !settings.locationRequestEnabled
                }"
              >
                <div class="parent-setting-item bg-white-custom form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="locationRequestEnabled"
                    name="locationRequestEnabled"
                    v-model="settings.locationRequestEnabled"
                    :checked="settings.locationRequestEnabled"
                    @change="touchSetting($event.target)"
                  />
                  <label class="form-check-label" for="locationRequestEnabled">
                    Location Enabled
                  </label>
                </div>
                <b-form-group label="Request Maximum Attempts">
                  <b-form-input
                    name="locationRequestMaxAttempts"
                    v-model="settings.locationRequestMaxAttempts"
                    ref="locationRequestMaxAttempts"
                    @change="touchSetting($refs.locationRequestMaxAttempts)"
                    :disabled="!settings.locationRequestEnabled"
                  ></b-form-input>
                </b-form-group>
                <b-form-group label="Request Timeout (in minutes)">
                  <b-form-input
                    name="locationRequestTimeout"
                    v-model="settings.locationRequestTimeout"
                    @change="touchSetting($refs.locationRequestTimeout)"
                    ref="locationRequestTimeout"
                    :disabled="!settings.locationRequestEnabled"
                  ></b-form-input>
                </b-form-group>
                <b-form-group label="Location Request Help Text">
                  <b-form-textarea
                    placeholder=""
                    rows="4"
                    name="locationRequestExplainMsg"
                    v-model="settings.locationRequestExplainMsg"
                    @change="touchSetting($refs.locationRequestExplainMsg)"
                    ref="locationRequestExplainMsg"
                    :disabled="!settings.locationRequestEnabled"
                  ></b-form-textarea>
                </b-form-group>
              </div>
            </div>
          </div>
        </div>
        <div class="admin-settings--actions pt-3">
          <b-button
            variant="success"
            pill
            :disabled="!settingsUpdated"
            class="save-settings"
            @click="save"
          >
            Save Settings
          </b-button>
        </div>
      </div>
    </b-overlay>

    <AgencySoundListener
      :soundSettings="localSoundSelectorSettings"
      scope="admin"
    />
  </div>
</template>

<script>
/* eslint-disable */
import "prismjs/themes/prism-coy.css";
import { mapGetters, mapActions, mapState } from "vuex";
import { EventBus } from "@/events/eventBus";
import { audioClipList } from "@/constants";
import AgencySoundListener from "@/components/AgencySoundListener";
import vSelect from "vue-select";
import { createPopper } from "@popperjs/core";

export default {
  name: "AdminSettings",
  components: { AgencySoundListener, vSelect },
  computed: {
    ...mapGetters("user", [
      "currentToken",
      "soundSelectorSetting",
      "volume",
      "isDarkMode",
    ]),
    ...mapGetters("agency", ["currentAgencyId"]),
    ...mapState({
      agencySettings: (state) => state.agency.settings,
      agency: (state) => state.agency.agency,
    }),
  },
  data() {
    return {
      localSettings: {},
      localSoundSelectorSettings: {},
      settingsUpdated: false,
      agencyUpdated: false,
      soundSelectorChanged: false,
      logoPreview: "/images/heroicon-badge.png",
      isLoading: false,
      vueEnvironment: process.env.NODE_ENV,
      badgeTheme: {
        sandbox: "warning",
        pending: "warning",
        provisioning: "warning",
        production: "success",
        qa: "info",
        unprovisioned: "warning",
        active: "success",
      },
      audioList: [...audioClipList],
      audioTesting: null,
      placement: 'top',
      settings: {},
      tabSelected: 0,
    };
  },
  created() {
    this.settings = {...this.agencySettings};
    if (this.settings.agencyLogo)
      this.logoPreview = this.settings.agencyLogo;

    this.localSoundSelectorSettings = { ...this.soundSelectorSetting };

    EventBus.$on("fileUploadStatus", (status, result) => {
      if (status === true) {
        this.localSettings.agencyLogo = result;
        this.settingsUpdated = true;
      } else {
        this.$toastr(
          "error",
          "An error occurred uploading logo image. Please try again. "
        );
        this.logoPreview = "/images/heroicon-badge.png";
      }
    });
    EventBus.$on("audio-playback-complete", () => {
      this.audioTesting = null;
    });
  },
  methods: {
    ...mapActions("agency", ["updateAgencySettings"]),
    triggerFileUpload() {
      this.$refs.agencyLogo.click();
    },
    async save() {
      let statusCode = null;
      if (this.settingsUpdated) {
        let payload = [];
        for (const [key, value] of Object.entries(this.localSettings))
          payload.push({ name: key, value: value, component: "agency" });

        if (this.soundSelectorChanged) {
          for (const [key, data] of Object.entries(
            this.localSoundSelectorSettings
          )) {
            const { enabled, value } = data;
            payload.push({
              name: key,
              value: !enabled ? "" : value,
              component: "agency",
            });
          }
        }

        try {
          this.isLoading = true;
          await this.$ConveyApi.settingsApi.updateAgencySettings({
            agencyId: this.currentAgencyId,
            payload,
          })
            .then((res) => {
              this.updateAgencySettings(res.data.data);
              statusCode = res.status;
            })
            .catch(() => {
              this.$toastr("error", "An error occurred. Please try again.");
            });

          if (this.agencyUpdated) {
            let payload = {};
            if (this.agency_name) payload.agency_name = this.agency_name;

            if (this.agency_domain) payload.agency_domain = this.agency_domain;

            await this.$ConveyApi.agenciesApi.updateAgency({
              agencyId: this.currentAgencyId,
              payload,
            })
              .then((res) => {
                statusCode = res.status;
              })
              .catch(() => {
                this.$toastr("error", "An error occurred. Please try again.");
              });
          }
        } catch (err) {
          this.$toastr("error", "An error occurred. Please try again.");
        } finally {
          this.isLoading = false;
        }
      }

      if (statusCode === 200) {
        this.$toastr("success", "Settings Updated.");
      } else {
        this.$toastr("error", "An error occurred. Please try again.");
      }
    },
    touchSetting(e, type = null) {
      console.log("touchSetting:e: ", e.name, e.type)
      console.log("touchSetting:type: ", type)
      if (type === "agency") {
        this.agencyUpdated = true;
        this[e.name] = e._data.vModelValue;
      }
      this.settingsUpdated = true;

      this.localSettings[e.name] =
        e.type === "checkbox" ? e.checked.toString() : e.value;
    },
    pickFile() {
      let input = this.$refs.agencyLogo;
      let files = input.files;

      if (files && files[0]) {
        let reader = new FileReader();
        reader.onload = (e) => {
          this.logoPreview = e.target.result;
        };

        reader.readAsDataURL(files[0]);
        EventBus.$emit(
          "fileUpload",
          files[0],
          `logos/${this.currentAgencyId}/${files[0].name}`,
          files[0].type
        );
      }
    },
    changeSoundSelectorSettings(fieldName, setting) {
      this.soundSelectorChanged = true;
      this.settingsUpdated = true;
      const originSettings = { ...this.localSoundSelectorSettings };
      const fieldSetting = { ...originSettings[fieldName], ...setting };
      const updatedSettings = { ...originSettings, [fieldName]: fieldSetting };
      this.localSoundSelectorSettings = { ...updatedSettings };
    },
    testAudio(key) {
      if (this.audioTesting && this.audioTesting.status === true) {
        this.audioTesting = null;
        const eventEmitter = {
          videoJoinedNotificationSound:
            "stop-video-joined-notification-sound-admin",
          videoLeftNotificationSound:
            "stop-video-left-notification-sound-admin",
          messageNotificationSound: "stop-message-notification-sound-admin",
          voiceNotificationSound: "stop-voice-notification-sound-admin",
          textNotificationSound: "stop-text-notification-sound-admin",
          textTo911NotificationSound:
            "stop-text-to-911-notification-sound-admin",
        };

        EventBus.$emit(eventEmitter[key]);
      } else {
        this.audioTesting = {
          status: true,
          type: key,
        };
        const eventEmitter = {
          videoJoinedNotificationSound:
            "play-video-joined-notification-sound-admin",
          videoLeftNotificationSound:
            "play-video-left-notification-sound-admin",
          messageNotificationSound: "play-message-notification-sound-admin",
          voiceNotificationSound: "play-voice-notification-sound-admin",
          textNotificationSound: "play-text-notification-sound-admin",
          textTo911NotificationSound:
            "play-text-to-911-notification-sound-admin",
        };
        EventBus.$emit(eventEmitter[key]);
      }
    },
    withPopper(dropdownList, component, { width }) {
      dropdownList.style.width = width
      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -1],
            },
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({ state }) {
              component.$el.classList.toggle(
                'drop-up',
                state.placement === 'top'
              )
            },
          },
        ],
      })
      return () => popper.destroy()
    },
  },
  watch: {
    agencySettings: {
      handler(settings) {
        this.settings = {...settings};
      },
      deep: true,
    },
  },
  beforeDestroy() {
    EventBus.$off("fileUploadStatus");
  },
};
</script>

<style lang="scss">
.admin-settings-wrapper {
  width: 100%;
  height: 100%;

  >.b-overlay-wrap {
    width: 100%;
    height: 100%;
  }
}
.admin-settings {
  width: 100%;
  height: 100%;
  border-radius: 1rem;

  input {
    border-radius: 1.5rem;
  }

  textarea {
    border-radius: 1rem;
  }

  .col-form-label,
  label {
    color: #232323;
    font-weight: 500;
  }
  .form-group {
    margin-bottom: 1rem;
  }
  .form-control:disabled {
    cursor: not-allowed;
  }
  .form-switch {
    display: inline-flex;
    align-items: center;
    .form-check-input {
      width: 2.5rem;
      height: 1.5rem;
    }
    .form-check-label {
      margin-left: 0.5rem;
    }
  }
  .logo-upload {
    justify-content: center;
    display: flex;
    .logo-responsive {
      max-width: 220px;
      max-height: 150px;
    }
    &--btn {
      color: #000;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
    &--label {
      font-size: 0.875rem;
      color: #4379ee;
      font-weight: 600;
      text-align: center;
      padding: 1rem;
      cursor: pointer;
      &.dark {
        color: #b2b9bf !important;
      }
    }
  }
  &.dark {
    .col-form-label,
    label {
      color: #b2b9bf !important;
      font-weight: 500;
    }
    input::placeholder {
      color: #b2b9bf !important;
      opacity: 0.5;
    }
    .v-select .vs__dropdown-toggle input.vs__search {
      background: #272b34;
    }
    .v-select .vs__dropdown-toggle {
      background: #272b34;
    }

    .vs--disabled .vs__dropdown-toggle,
    .vs--disabled .vs__open-indicator,
    .vs--disabled .vs__search,
    .vs--disabled .vs__selected {
      background-color: #272b34;
      cursor: var(--vs-disabled-cursor);
    }
  }
  &--setting-tabs {
    width: 100%;
    height: calc(100% - 60px);

    &--header {
      width: 100%;
      height: auto;
      padding-left: 8px;
      flex-wrap: nowrap;

      .settings-tab-header {
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 1rem;
        font-weight: 500;
        cursor: pointer;
        border-bottom: 2px solid transparent;
      }

      @media (max-width: 767px) {
        flex-wrap: wrap;
        .settings-tab-header {
          height: 40px;
          padding: 0.5rem 1rem;
        }
      }
    }

    &--content {
      flex: 1;
      overflow: hidden;
      overflow-y: auto;
      padding: 1rem;
      border-radius: 8px;

      // .form-check-input:checked {
      //   background-color: #e9ab2e !important;
      //   border-color: #e9ab2e !important;
      // }

      .vs__dropdown-toggle {
        border: 1px solid #e9ecef !important;
        border-radius: 1.5rem !important;
      }

      .settings-tab-content {
        padding-top: 1rem;

        &.flexible {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;

          > div {
            width: 100%;
            max-width: 600px;
          }
        }
      }
    }
  }
  &--actions {
    width: 100%;
    height: 60px;
    .save-settings {
      float: right;
    }
  }
  &--tenant-info {
    &.row {
      margin: 0 -0.5rem !important;
      > * {
        padding: 0 0.5rem !important;
      }
    }
    .setting-item {
      height: 100%;
      padding: 0.5rem;
      border: 1px solid #e9ecef;
      border-radius: 1rem;
      text-align: center;
      align-items: center;
      display: flex;
      justify-content: center;
      &--content {
        text-align: center;
        justify-content: center;
        .badge {
          padding: 5px 15px;
          text-transform: capitalize;
        }
      }
      &.dark {
        border: 1px solid rgba(255, 255, 255, 0.2);
      }
    }
  }
  &--tenant-settings {
    &.row {
      margin: 0 -0.5rem !important;
      > * {
        padding: 0 0.5rem !important;
      }
    }

  }
  &--settings-group {
    position: relative;
    padding: 2rem 1rem 1rem;
    border-radius: 1rem;
    width: 100%;
    max-width: 600px;

    .parent-setting-item {
      position: absolute;
      top: -0.75rem;
      left: 1rem;
      margin: 0;
    }

    > * {
      opacity: 1;
      &:first-child {
        &.form-switch {
          padding-left: 2.75rem;
          padding-right: 0.25rem;
        }
      }
      &:last-child {
        margin-bottom: 0;
      }
    }

    &.disabled {
      > * {
        opacity: 0.5;
        &:first-child {
          opacity: 1;
        }
      }
    }
  }
}
</style>
